/* global HSCore */

import React, { useContext, useEffect, useRef } from "react";
import { sortBy, max, size } from "lodash";
import LanguageContext from "../../utils/Contexts/LanguageContext";

function DomainRating({ domainRating, flag }) {
  const { t } = useContext(LanguageContext);

  let historicalDataDR = domainRating?.history?.dr || [];
  historicalDataDR = sortBy(historicalDataDR, "date");
  const labelsDR = historicalDataDR.map((entry) =>
    new Date(entry.date).toLocaleString("default", {
      timeZone: "UTC",
      month: "short",
    })
  );
  let valuesDR = historicalDataDR.map((entry) => entry.value);
  if (!valuesDR.length) valuesDR = [0];
  const numberOfDrMonths = labelsDR.length;

  let historicalDataDA = domainRating?.history?.da || [];
  historicalDataDA = sortBy(historicalDataDA, "date");
  const labelsDA = historicalDataDA.map((entry) =>
    new Date(entry.date).toLocaleString("default", {
      timeZone: "UTC",
      month: "short",
    })
  );
  let valuesDA = historicalDataDA.map((entry) => entry.value);
  if (!valuesDA.length) valuesDA = [0];
  const numberOfDaMonths = labelsDA.length;

  let historicalDataTraffic = domainRating?.history?.traffic || [];
  historicalDataTraffic = sortBy(historicalDataTraffic, "date");
  const labelsTraffic = historicalDataTraffic.map((entry) =>
    new Date(entry.date).toLocaleString("default", {
      timeZone: "UTC",
      month: "short",
    })
  );
  let valuesTraffic = historicalDataTraffic.map((entry) => entry.value);
  if (!valuesTraffic.length) valuesTraffic = [0];
  const numberOfTrafficMonths = labelsTraffic.length;

  let historicalDataPricesMin = domainRating?.history?.prices?.links?.min || [];
  historicalDataPricesMin = sortBy(historicalDataPricesMin, "date");
  const labelsPricesMin = historicalDataPricesMin.map((entry) =>
    new Date(entry.date).toLocaleString("default", {
      timeZone: "UTC",
      month: "short",
    })
  );
  let valuesPricesMin = historicalDataPricesMin.map((entry) => entry.value);
  if (!valuesPricesMin.length) valuesPricesMin = [0];
  const numberOfPricesMinMonths = labelsPricesMin.length;
  const minPriceValue =
    valuesPricesMin.length > 0 ? Math.min(...valuesPricesMin) : "";

  let historicalDataPricesMax = domainRating?.history?.prices?.links?.max || [];
  historicalDataPricesMax = sortBy(historicalDataPricesMax, "date");
  const labelsPricesMax = historicalDataPricesMax.map((entry) =>
    new Date(entry.date).toLocaleString("default", {
      timeZone: "UTC",
      month: "short",
    })
  );
  let valuesPricesMax = historicalDataPricesMax.map((entry) => entry.value);
  if (!valuesPricesMax.length) valuesPricesMax = [0];
  const numberOfPricesMaxMonths = labelsPricesMax.length;
  const maxPriceValue =
    valuesPricesMax.length > 0 ? Math.max(...valuesPricesMax) : "";

  const calculateGrowthPercentage = (values) => {
    let growthPercentage = 0;
    if (values.length > 1) {
      const oldValue = values[0];
      const newValue = values[values.length - 1]; // Current month value

      if (oldValue !== 0) {
        growthPercentage = ((newValue - oldValue) / oldValue) * 100;
      } else {
        growthPercentage = newValue > 0 ? 100 : 0;
      }
    }
    return growthPercentage;
  };

  const growthPercentageDR = calculateGrowthPercentage(valuesDR);
  const growthPercentageDA = calculateGrowthPercentage(valuesDA);
  const growthPercentageTraffic = calculateGrowthPercentage(valuesTraffic);
  const growthPercentageMinValue = calculateGrowthPercentage(valuesPricesMin);
  const growthPercentageMaxValue = calculateGrowthPercentage(valuesPricesMax);
  const averageGrowthPrice = (
    (growthPercentageMinValue + growthPercentageMaxValue) /
    2
  ).toFixed(1); // Rounded to one decimal place

  const hsInitRef = useRef(null);
  useEffect(() => {
    if (
      size(domainRating) &&
      valuesPricesMax.length > 0 &&
      valuesPricesMin.length > 0 &&
      valuesDR.length > 0 &&
      valuesDA.length > 0 &&
      valuesTraffic.length > 0 &&
      !hsInitRef.current
    ) {
      HSCore.components.HSChartJS.init(".js-chart");
      hsInitRef.current = true;
    }
  }, [
    domainRating,
    valuesDR,
    valuesDA,
    valuesTraffic,
    valuesPricesMax,
    valuesPricesMin,
  ]);

  function formatPrice(value) {
    if (value >= 1000) {
      return `${(value / 1000).toFixed(2)}K`;
    }
    return value;
  }

  return (
    <div className="row" style={{ paddingBlock: "3px" }}>
      <div className="col-sm-6 mb-4 mb-xl-0 col-xl-3 domain-rating-parent-two">
        <div className="card card-hover-shadow domain-rating-height">
          <div className="px-3 pt-3 d-flex flex-column">
            <h6 className={`card-expiry-balance`} style={{ color: "#6C747A" }}>
              {t("Domain Rating")}
            </h6>

            <div className="row align-items-center gx-2 mb-1">
              <div className="col-6">
                <h2 className="domain-rating-title">{domainRating?.dr}</h2>
              </div>

              <div className="col-6">
                <div className="chartjs-custom" style={{ height: "3rem" }}>
                  <canvas
                    className="js-chart"
                    data-hs-chartjs-options={JSON.stringify({
                      type: "line",
                      data: {
                        labels: labelsDR.length > 0 ? labelsDR : ["No data"], // Fallback for no data
                        datasets: [
                          {
                            data: valuesDR.length > 0 ? valuesDR : [0], // Fallback for no data
                            backgroundColor: [
                              "rgba(55, 125, 255, 0)",
                              "rgba(255, 255, 255, 0)",
                            ],
                            borderColor: "#377dff",
                            borderWidth: 2,
                            pointRadius: 0,
                            pointHoverRadius: 0,
                          },
                        ],
                      },
                      options: {
                        scales: {
                          y: {
                            display: false,
                          },
                          x: {
                            display: false,
                          },
                        },
                        hover: {
                          mode: "average",
                          intersect: false,
                        },
                        plugins: {
                          tooltip: {
                            enabled: true,
                            mode: "index",
                            intersect: false,
                            titleColor: "#fff",
                            bodyColor: "#fff",
                            borderWidth: 6,
                            displayColors: true,
                            usePointStyle: true,
                            boxWidth: 5,
                            boxHeight: 5,
                            boxPadding: 7,
                            caretSize: 0,
                          },
                        },
                      },
                    })}
                  ></canvas>
                </div>
              </div>
            </div>
            <div className="d-flex align-items-center">
              <span
                style={{
                  fontSize: "12px",
                  fontWeight: "600",
                }}
                className={`badge ${
                  growthPercentageDR < 0
                    ? "bg-soft-danger text-danger"
                    : growthPercentageDR >= 0
                    ? flag === true
                      ? "bg-soft-primary text-primary"
                      : "bg-soft-success text-success"
                    : ""
                }`}
              >
                <i
                  className={`bi ${
                    growthPercentageDR < 0
                      ? flag === true
                        ? "bi-bar-chart-line"
                        : "bi-graph-down"
                      : flag === true
                      ? "bi-bar-chart-line"
                      : "bi-graph-up"
                  }`}
                ></i>{" "}
                {growthPercentageDR.toFixed(1)}%
              </span>
              <span
                className="ms-2"
                style={{
                  fontSize: "12px",
                  fontWeight: "400",
                  color: "#919293",
                }}
              >
                {t("last")} {numberOfDrMonths}{" "}
                {numberOfDrMonths === 1 ? t("month") : t("months")}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="col-sm-6 col-xl-3 mb-4 mb-xl-0 domain-rating-parent">
        <div className="card card-hover-shadow domain-rating-height">
          <div className="px-3 pt-3 d-flex flex-column">
            <h6 className={`card-expiry-balance`} style={{ color: "#6C747A" }}>
              {t("Domain Authority")}
            </h6>

            <div className="row align-items-center gx-2 mb-1">
              <div className="col-6">
                <h2 className="domain-rating-title">{domainRating?.da}</h2>
              </div>

              <div className="col-6">
                <div className="chartjs-custom" style={{ height: "3rem" }}>
                  <canvas
                    className="js-chart"
                    data-hs-chartjs-options={JSON.stringify({
                      type: "line",
                      data: {
                        labels: labelsDA.length > 0 ? labelsDA : ["No data"],
                        datasets: [
                          {
                            data: valuesDA.length > 0 ? valuesDA : [0],
                            backgroundColor: [
                              "rgba(55, 125, 255, 0)",
                              "rgba(255, 255, 255, 0)",
                            ],
                            borderColor: "#377dff",
                            borderWidth: 2,
                            pointRadius: 0,
                            pointHoverRadius: 0,
                          },
                        ],
                      },
                      options: {
                        scales: {
                          y: {
                            display: false,
                          },
                          x: {
                            display: false,
                          },
                        },
                        hover: {
                          mode: "average",
                          intersect: false,
                        },
                        plugins: {
                          tooltip: {
                            enabled: true,
                            mode: "index",
                            intersect: false,
                            titleColor: "#fff",
                            bodyColor: "#fff",
                            borderWidth: 6,
                            displayColors: true,
                            usePointStyle: true,
                            boxWidth: 5,
                            boxHeight: 5,
                            boxPadding: 7,
                            caretSize: 0,
                          },
                        },
                      },
                    })}
                  ></canvas>
                </div>
              </div>
            </div>
            <div className="d-flex align-items-center">
              <span
                style={{
                  fontSize: "12px",
                  fontWeight: "600",
                }}
                className={`badge ${
                  growthPercentageDA < 0
                    ? "bg-soft-danger text-danger"
                    : growthPercentageDA >= 0
                    ? flag === true
                      ? "bg-soft-primary text-primary"
                      : "bg-soft-success text-success"
                    : ""
                }`}
              >
                <i
                  className={`bi ${
                    growthPercentageDA < 0
                      ? flag === true
                        ? "bi-bar-chart-line"
                        : "bi-graph-down"
                      : flag === true
                      ? "bi-bar-chart-line"
                      : "bi-graph-up"
                  }`}
                ></i>{" "}
                {growthPercentageDA.toFixed(1)}%
              </span>
              <span
                className="ms-2"
                style={{
                  fontSize: "12px",
                  fontWeight: "400",
                  color: "#919293",
                }}
              >
                {t("last")} {numberOfDaMonths}{" "}
                {numberOfDaMonths === 1 ? t("month") : t("months")}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="col-sm-6 col-xl-3 mb-4 mb-sm-0 domain-rating-parent-two">
        <div className="card card-hover-shadow domain-rating-height">
          <div className="px-3 pt-3 d-flex flex-column">
            <h6 className={`card-expiry-balance`} style={{ color: "#6C747A" }}>
              {t("Traffic")}
            </h6>

            <div className="row align-items-center gx-2 mb-1">
              <div className="col-6">
                <h2 className="domain-rating-title">
                  {domainRating?.traffic?.toLocaleString("en-US")}
                </h2>
              </div>

              <div className="col-6">
                <div className="chartjs-custom" style={{ height: "3rem" }}>
                  <canvas
                    className="js-chart"
                    data-hs-chartjs-options={JSON.stringify({
                      type: "line",
                      data: {
                        labels:
                          labelsTraffic.length > 0
                            ? labelsTraffic
                            : ["No data"], // Fallback for no data
                        datasets: [
                          {
                            data:
                              valuesTraffic.length > 0 ? valuesTraffic : [0], // Fallback for no data
                            backgroundColor: [
                              "rgba(55, 125, 255, 0)",
                              "rgba(255, 255, 255, 0)",
                            ],
                            borderColor: "#377dff",
                            borderWidth: 2,
                            pointRadius: 0,
                            pointHoverRadius: 0,
                          },
                        ],
                      },
                      options: {
                        scales: {
                          y: {
                            display: false,
                          },
                          x: {
                            display: false,
                          },
                        },
                        hover: {
                          mode: "average",
                          intersect: false,
                        },
                        plugins: {
                          tooltip: {
                            enabled: true,
                            mode: "index",
                            intersect: false,
                            titleColor: "#fff",
                            bodyColor: "#fff",
                            borderWidth: 6,
                            displayColors: true,
                            usePointStyle: true,
                            boxWidth: 5,
                            boxHeight: 5,
                            boxPadding: 7,
                            caretSize: 0,
                          },
                        },
                      },
                    })}
                  ></canvas>
                </div>
              </div>
            </div>
            <div className="d-flex align-items-center">
              <span
                style={{
                  fontSize: "12px",
                  fontWeight: "600",
                }}
                className={`badge ${
                  growthPercentageTraffic < 0
                    ? "bg-soft-danger text-danger"
                    : growthPercentageTraffic >= 0
                    ? flag === true
                      ? "bg-soft-primary text-primary"
                      : "bg-soft-success text-success"
                    : ""
                }`}
              >
                <i
                  className={`bi ${
                    growthPercentageTraffic < 0
                      ? flag === true
                        ? "bi-bar-chart-line"
                        : "bi-graph-down"
                      : flag === true
                      ? "bi-bar-chart-line"
                      : "bi-graph-up"
                  }`}
                ></i>{" "}
                {growthPercentageTraffic.toFixed(1)}%
              </span>
              <span
                className="ms-2"
                style={{
                  fontSize: "12px",
                  fontWeight: "400",
                  color: "#919293",
                }}
              >
                {t("last")} {numberOfTrafficMonths}{" "}
                {numberOfTrafficMonths === 1 ? t("month") : t("months")}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="col-sm-6 col-xl-3 domain-rating-parent">
        <div className="card card-hover-shadow domain-rating-height">
          <div className="px-3 pt-3 d-flex flex-column">
            <h6 className={`card-expiry-balance`} style={{ color: "#6C747A" }}>
              {t("Price Ranges")}
            </h6>

            <div className="row align-items-center gx-2 mb-1">
              <div className="col-6">
                <h2 className="domain-rating-title">
                  {" "}
                  ${formatPrice(minPriceValue || 0)}-$
                  {formatPrice(maxPriceValue || 0)}
                </h2>
              </div>

              <div className="col-6">
                <div className="chartjs-custom" style={{ height: "3rem" }}>
                  <canvas
                    className="js-chart"
                    data-hs-chartjs-options={JSON.stringify({
                      type: "line",
                      data: {
                        labels:
                          labelsPricesMax.length === 1
                            ? [labelsPricesMax[0], labelsPricesMax[0]]
                            : labelsPricesMax.length > 0
                            ? labelsPricesMax
                            : ["No data"],
                        datasets: [
                          {
                            data:
                              valuesPricesMax.length === 1
                                ? [valuesPricesMax[0], valuesPricesMax[0]]
                                : valuesPricesMax.length > 0
                                ? valuesPricesMax
                                : [0],
                            backgroundColor: "rgba(55, 125, 255, 0)",
                            borderColor: "#377dff",
                            borderWidth: 2,
                            pointRadius: 0,
                            pointHoverRadius: 3,
                            tension: 0,
                          },
                          {
                            data:
                              valuesPricesMin.length === 1
                                ? [valuesPricesMin[0], valuesPricesMin[0]]
                                : valuesPricesMin.length > 0
                                ? valuesPricesMin
                                : [0],
                            backgroundColor: "rgba(255, 255, 255, 0)",
                            borderColor: "#00c9a7",
                            borderWidth: 2,
                            pointRadius: 0,
                            pointHoverRadius: 3,
                            tension: 0,
                          },
                        ],
                      },
                      options: {
                        responsive: true,
                        maintainAspectRatio: false,
                        layout: {
                          padding: {
                            top: 2,
                            right: 0,
                            bottom: 2, // Adjust this value as needed
                            left: 0,
                          },
                        },
                        scales: {
                          y: {
                            display: false,
                          },
                          x: {
                            display: false,
                          },
                        },
                        hover: {
                          mode: "index",
                          intersect: false,
                        },
                        plugins: {
                          tooltip: {
                            enabled: true,
                            mode: "index",
                            intersect: false,
                            titleColor: "#fff",
                            bodyColor: "#fff",
                            borderWidth: 6,
                            displayColors: true,
                            usePointStyle: true,
                            boxWidth: 5,
                            boxHeight: 5,
                            boxPadding: 7,
                            caretSize: 0,
                          },
                        },
                      },
                    })}
                  ></canvas>
                </div>
              </div>
            </div>
            <div className="d-flex align-items-center">
              <span
                style={{
                  fontSize: "12px",
                  fontWeight: "600",
                }}
                className={`badge ${
                  averageGrowthPrice < 0
                    ? "bg-soft-danger text-danger"
                    : averageGrowthPrice >= 0
                    ? flag === true
                      ? "bg-soft-primary text-primary"
                      : "bg-soft-success text-success"
                    : ""
                }`}
              >
                <i
                  className={`bi ${
                    averageGrowthPrice < 0
                      ? flag === true
                        ? "bi-bar-chart-line"
                        : "bi-graph-down"
                      : flag === true
                      ? "bi-bar-chart-line"
                      : "bi-graph-up"
                  }`}
                ></i>{" "}
                {averageGrowthPrice}%
              </span>
              <span
                className="ms-2"
                style={{
                  fontSize: "12px",
                  fontWeight: "400",
                  color: "#919293",
                }}
              >
                {t("last")}{" "}
                {max([numberOfPricesMinMonths, numberOfPricesMaxMonths])}{" "}
                {max([numberOfPricesMinMonths, numberOfPricesMaxMonths]) === 1
                  ? t("month")
                  : t("months")}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DomainRating;
